/**
 * Portfolio Holding Service (v3)
 *
 * Replaces investmentService.js for all CRUD on portfolio_holdings + portfolio_transactions.
 * Adds source-priority dedup, buy/sell with avg-price recalc, Quick Entry mode.
 *
 * investmentService.js is preserved as a read-only fallback for 30 days.
 */
import { supabase, isSupabaseConfigured } from './supabaseClient';
import { fetchUSDtoINR } from './stockPriceService';

// ===========================
// Constants
// ===========================
const LS_KEY = 'finncal_portfolio_holdings';

// Dedup composite-key definitions by asset type
const DEDUP_KEYS = {
  STOCK: ['user_id', 'family_member_id', 'isin', 'symbol', 'broker'],
  STOCK_INDIAN: ['user_id', 'family_member_id', 'isin', 'symbol', 'broker'],
  STOCK_US: ['user_id', 'family_member_id', 'symbol', 'exchange'],
  MF: ['user_id', 'family_member_id', 'isin', 'folio_number'],
  NPS: ['user_id', 'family_member_id', 'asset_type', 'name'],
  EPF: ['user_id', 'family_member_id', 'asset_type'],
  PPF: ['user_id', 'family_member_id', 'asset_type'],
  FD: ['user_id', 'family_member_id', 'name', 'maturity_date'],
  GOLD_PHYSICAL: ['user_id', 'family_member_id', 'asset_type', 'name'],
  GOLD_DIGITAL: ['user_id', 'family_member_id', 'asset_type', 'name'],
  SILVER: ['user_id', 'family_member_id', 'asset_type', 'name'],
  REALESTATE_RESIDENTIAL: ['user_id', 'family_member_id', 'asset_type', 'name'],
  REALESTATE_COMMERCIAL: ['user_id', 'family_member_id', 'asset_type', 'name'],
  REALESTATE_LAND: ['user_id', 'family_member_id', 'asset_type', 'name'],
};

// Fallback key for any type not listed above
const DEFAULT_DEDUP_KEYS = ['user_id', 'family_member_id', 'asset_type', 'name'];

// ===========================
// Helper: resolve SELF family member
// ===========================
const resolveFamilyMemberId = async (userId, familyMemberId) => {
  if (familyMemberId) return familyMemberId;
  try {
    const { data } = await supabase
      .from('family_members')
      .select('id')
      .eq('user_id', userId)
      .eq('relationship', 'SELF')
      .single();
    return data?.id || null;
  } catch {
    return null;
  }
};

// ===========================
// Helper: get source priority number
// ===========================
export const getSourcePriority = (assetType, sourceIdentifier) => {
  const MAP = {
    NSDL_CAS: 1,
    CDSL_CAS: 1,
    AMC_API: 2,
    BROKER_ZERODHA: 2,
    BROKER_VESTED: 2,
    CDSL_DEMAT: 1,
    NSDL_DEMAT: 1,
    EXCEL_UPLOAD: 4,
    TRADEBOOK_UPLOAD: 4,
    MANUAL: 5,
    USER_ADJUSTED: 5,
    HOLDINGS_IMPORT: 6,
    QUICK_ENTRY: 10,
  };
  return MAP[sourceIdentifier] ?? 5;
};

// ===========================
// Helper: build dedup query
// ===========================
const findDuplicate = async (holding, userId, familyMemberId) => {
  const keys = DEDUP_KEYS[holding.asset_type] || DEFAULT_DEDUP_KEYS;
  let query = supabase.from('portfolio_holdings').select('*').eq('user_id', userId);

  for (const key of keys) {
    if (key === 'user_id') continue; // already applied
    if (key === 'family_member_id') {
      if (familyMemberId) {
        query = query.eq('family_member_id', familyMemberId);
      } else {
        query = query.is('family_member_id', null);
      }
    } else {
      const val = holding[key];
      if (val !== undefined && val !== null && val !== '') {
        query = query.eq(key, val);
      } else {
        query = query.is(key, null);
      }
    }
  }

  const { data } = await query.limit(1).single();
  return data || null;
};

// ===========================
// 1. getHoldings — replaces getInvestments()
// ===========================
export const getHoldings = async (familyMemberId = null) => {
  if (!isSupabaseConfigured()) {
    return getHoldingsFromLocalStorage();
  }

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated', data: [] };

    // Check if this user is a viewer linked to another account's portfolio
    let targetUserId = user.id;
    let isLinkedView = false;
    const { data: familyLink } = await supabase
      .from('user_family_links')
      .select('owner_user_id')
      .eq('viewer_user_id', user.id)
      .limit(1)
      .maybeSingle();

    if (familyLink?.owner_user_id) {
      targetUserId = familyLink.owner_user_id;
      isLinkedView = true;
    }

    // Paginated fetch — Supabase default limit is 1000 rows
    const PAGE_SIZE = 1000;
    let allData = [];
    let offset = 0;

    while (true) {
      let query = supabase
        .from('portfolio_holdings')
        .select('*')
        .eq('user_id', targetUserId)
        .order('current_value', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);

      if (familyMemberId) {
        query = query.eq('family_member_id', familyMemberId);
      }

      const { data: page, error } = await query;

      if (error) {
        console.error('Error fetching holdings:', error);
        if (error.code === '42P01') return getHoldingsFromLocalStorage();
        return { success: false, error: error.message, data: [] };
      }

      if (!page || page.length === 0) break;
      allData.push(...page);
      if (page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    // Backward-compat aliases so consumers can still read .units / .avg_cost
    const aliased = allData.map((h) => ({
      ...h,
      units: h.quantity,
      avg_cost: h.avg_buy_price,
      investment_name: h.name,
    }));

    return {
      success: true,
      data: aliased,
      isLinkedView,
      ownerUserId: isLinkedView ? targetUserId : null,
    };
  } catch (err) {
    console.error('Error in getHoldings:', err);
    return { success: false, error: err.message, data: [] };
  }
};

// ===========================
// 2. upsertHolding — with source-priority dedup
// ===========================
export const upsertHolding = async (holding, familyMemberId = null) => {
  if (!isSupabaseConfigured()) {
    return addHoldingToLocalStorage(holding);
  }

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const effectiveFMId = await resolveFamilyMemberId(
      user.id,
      familyMemberId || holding.family_member_id
    );

    const row = {
      user_id: user.id,
      family_member_id: effectiveFMId,
      asset_type: holding.asset_type || holding.investment_type || 'OTHER',
      isin: holding.isin || null,
      symbol: holding.symbol || null,
      amfi_code: holding.amfi_code || null,
      yahoo_symbol: holding.yahoo_symbol || null,
      name: holding.name,
      exchange: holding.exchange || null,
      quantity: holding.quantity ?? holding.units ?? null,
      avg_buy_price: holding.avg_buy_price ?? holding.avg_cost ?? null,
      invested_amount: holding.invested_amount || holding.invested_value || 0,
      current_price: holding.current_price || null,
      current_value: holding.current_value || 0,
      interest_rate: holding.interest_rate || null,
      maturity_date: holding.maturity_date || null,
      source: holding.source || 'MANUAL',
      source_identifier: holding.source_identifier || 'MANUAL',
      source_priority:
        holding.source_priority ??
        getSourcePriority(holding.asset_type, holding.source_identifier || 'MANUAL'),
      folio_number: holding.folio_number || null,
      demat_account: holding.demat_account || null,
      broker: holding.broker || null,
      currency: holding.currency || 'INR',
      is_locked: holding.is_locked || false,
    };

    // Check for existing duplicate
    const existing = await findDuplicate(row, user.id, effectiveFMId);

    if (existing) {
      // Source-priority: lower number = higher trust
      if (existing.is_locked) {
        return { success: true, data: existing, skipped: true, reason: 'locked' };
      }
      if (row.source_priority > existing.source_priority) {
        // Even if overall priority is lower, backfill missing cost-basis fields
        // CAS owns quantity/price (authoritative), broker/tradebook owns cost basis
        const costBasisUpdate = {};
        if (
          (!existing.invested_amount || existing.invested_amount === 0) &&
          row.invested_amount > 0
        ) {
          costBasisUpdate.invested_amount = row.invested_amount;
        }
        if (!existing.avg_buy_price && row.avg_buy_price > 0) {
          costBasisUpdate.avg_buy_price = row.avg_buy_price;
        }
        if (Object.keys(costBasisUpdate).length > 0) {
          await supabase
            .from('portfolio_holdings')
            .update({ ...costBasisUpdate, updated_at: new Date().toISOString() })
            .eq('id', existing.id);
          const merged = { ...existing, ...costBasisUpdate };
          return { success: true, data: merged, skipped: false, reason: 'cost_basis_backfilled' };
        }
        return { success: true, data: existing, skipped: true, reason: 'lower_priority' };
      }

      // Update existing
      const { data, error } = await supabase
        .from('portfolio_holdings')
        .update({
          ...row,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) return { success: false, error: error.message };
      return {
        success: true,
        data: { ...data, units: data.quantity, avg_cost: data.avg_buy_price },
      };
    }

    // Insert new
    const { data, error } = await supabase.from('portfolio_holdings').insert(row).select().single();

    if (error) {
      console.error('Error inserting holding:', error);
      if (error.code === '42P01') return addHoldingToLocalStorage(holding);
      return { success: false, error: error.message };
    }
    return { success: true, data: { ...data, units: data.quantity, avg_cost: data.avg_buy_price } };
  } catch (err) {
    console.error('Error in upsertHolding:', err);
    return { success: false, error: err.message };
  }
};

// ===========================
// 3. upsertHoldingsBatch — replaces saveInvestments()
// ===========================
export const upsertHoldingsBatch = async (holdings, familyMemberId = null, source = 'MANUAL') => {
  if (!isSupabaseConfigured()) {
    return saveHoldingsToLocalStorage(holdings);
  }

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const batchFMId = await resolveFamilyMemberId(user.id, familyMemberId);
    const allSaved = [];
    let skipped = 0;
    const errors = [];

    for (const h of holdings) {
      // Per-holding family_member_id takes priority over batch-level default
      const holdingFMId = h.family_member_id || batchFMId;
      const result = await upsertHolding(
        {
          ...h,
          source: h.source || source,
          source_identifier: h.source_identifier || source,
          source_priority:
            h.source_priority ?? getSourcePriority(h.asset_type, h.source_identifier || source),
        },
        holdingFMId
      );

      if (result.success) {
        if (result.skipped) {
          skipped++;
        } else {
          allSaved.push(result.data);
        }
      } else {
        console.error('Failed to upsert holding:', h.name, result.error);
        errors.push(`${h.name}: ${result.error}`);
      }
    }

    console.log(
      `Batch upsert: ${allSaved.length} saved, ${skipped} skipped, ${errors.length} failed (total: ${holdings.length})`
    );

    // If nothing was saved and input was non-empty — surface the error instead of silent success
    if (allSaved.length === 0 && skipped === 0 && holdings.length > 0) {
      const firstError = errors[0] || 'All holdings failed to save';
      return {
        success: false,
        error: firstError,
        allErrors: errors,
        savedCount: 0,
      };
    }

    return { success: true, data: allSaved, skipped, savedCount: allSaved.length };
  } catch (err) {
    console.error('Error in upsertHoldingsBatch:', err);
    return { success: false, error: err.message };
  }
};

// ===========================
// 4. updateHolding — replaces updateInvestment()
// ===========================
export const updateHolding = async (id, updates) => {
  if (!isSupabaseConfigured()) {
    return updateHoldingInLocalStorage(id, updates);
  }

  try {
    const updateData = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.quantity !== undefined || updates.units !== undefined) {
      updateData.quantity = updates.quantity ?? updates.units;
    }
    if (updates.avg_buy_price !== undefined || updates.avg_cost !== undefined) {
      updateData.avg_buy_price = updates.avg_buy_price ?? updates.avg_cost;
    }
    if (updates.invested_amount !== undefined) updateData.invested_amount = updates.invested_amount;
    if (updates.current_price !== undefined) updateData.current_price = updates.current_price;
    if (updates.current_value !== undefined) updateData.current_value = updates.current_value;
    if (updates.interest_rate !== undefined) updateData.interest_rate = updates.interest_rate;
    if (updates.maturity_date !== undefined) updateData.maturity_date = updates.maturity_date;

    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('portfolio_holdings')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating holding:', error);
      return { success: false, error: error.message };
    }
    return { success: true, data: { ...data, units: data.quantity, avg_cost: data.avg_buy_price } };
  } catch (err) {
    console.error('Error in updateHolding:', err);
    return { success: false, error: err.message };
  }
};

// ===========================
// 5. deleteHolding — replaces deleteInvestment()
//    Also cleans up related transactions and corporate actions for stocks
// ===========================
export const deleteHolding = async (id) => {
  if (!isSupabaseConfigured()) {
    return deleteHoldingFromLocalStorage(id);
  }

  try {
    // First, fetch the holding to get symbol, family_member_id, user_id for cleanup
    const { data: holding, error: fetchErr } = await supabase
      .from('portfolio_holdings')
      .select('symbol, family_member_id, user_id, asset_type')
      .eq('id', id)
      .single();

    if (fetchErr) {
      console.error('Error fetching holding for delete:', fetchErr);
      return { success: false, error: fetchErr.message };
    }

    // Delete the holding
    const { error } = await supabase.from('portfolio_holdings').delete().eq('id', id);

    if (error) {
      console.error('Error deleting holding:', error);
      return { success: false, error: error.message };
    }

    // For stock holdings, also clean up transactions and corporate actions
    if (holding && ['STOCK', 'STOCK_INDIAN', 'STOCK_US'].includes(holding.asset_type)) {
      // Delete all transactions for this symbol + family member
      const { error: txnErr } = await supabase
        .from('portfolio_transactions')
        .delete()
        .eq('family_member_id', holding.family_member_id)
        .eq('symbol', holding.symbol);
      if (txnErr)
        console.warn('Error cleaning up transactions for', holding.symbol, txnErr.message);

      // Reset corporate action status for this symbol
      const { error: caErr } = await supabase
        .from('user_corporate_actions')
        .delete()
        .eq('family_member_id', holding.family_member_id)
        .eq('symbol', holding.symbol);
      if (caErr) console.warn('Error cleaning up corp actions for', holding.symbol, caErr.message);
    }

    return { success: true };
  } catch (err) {
    console.error('Error in deleteHolding:', err);
    return { success: false, error: err.message };
  }
};

// ===========================
// 5b. deleteBrokerHoldings — delete all stock holdings for (member, broker)
// ===========================
export const deleteBrokerHoldings = async (familyMemberId, broker, isins = []) => {
  try {
    // Delete all holdings for this (member, broker)
    const { error } = await supabase
      .from('portfolio_holdings')
      .delete()
      .eq('family_member_id', familyMemberId)
      .eq('broker', broker)
      .in('asset_type', ['STOCK', 'STOCK_INDIAN', 'STOCK_US']);

    if (error) {
      console.error('Error deleting broker holdings:', error);
      return { error };
    }

    // Also clean up legacy null-broker holdings for same ISINs
    // (prevents duplicates on first broker-scoped upload)
    if (isins.length > 0) {
      await supabase
        .from('portfolio_holdings')
        .delete()
        .eq('family_member_id', familyMemberId)
        .is('broker', null)
        .in('isin', isins)
        .in('asset_type', ['STOCK', 'STOCK_INDIAN', 'STOCK_US']);
    }

    return { error: null };
  } catch (err) {
    console.error('Error in deleteBrokerHoldings:', err);
    return { error: err.message };
  }
};

// ===========================
// 6. lockHolding
// ===========================
export const lockHolding = async (id, locked = true) => {
  try {
    const { data, error } = await supabase
      .from('portfolio_holdings')
      .update({ is_locked: locked, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

// ===========================
// 7. processBuyTransaction — avg price recalc
// ===========================
export const processBuyTransaction = async (txn) => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const effectiveFMId = await resolveFamilyMemberId(user.id, txn.family_member_id);

    // STOCK_US holdings store avg_buy_price in native USD (matching the
    // TRADEBOOK_UPLOAD/HOLDINGS_IMPORT convention) but invested_amount/current_value
    // in INR — the nightly price job (scheduled-price-update.js) writes
    // current_price/current_value in INR and never touches invested_amount, so it
    // must already be INR at creation time or gain calculations compare USD to INR.
    const usdInrRate = txn.asset_type === 'STOCK_US' ? await fetchUSDtoINR() : 1;

    // Insert transaction
    const txnRow = {
      user_id: user.id,
      family_member_id: effectiveFMId,
      asset_type: txn.asset_type,
      symbol: txn.symbol || null,
      isin: txn.isin || null,
      yahoo_symbol: txn.yahoo_symbol || null,
      name: txn.name || txn.asset_type,
      exchange: txn.exchange || null,
      txn_type: 'BUY',
      txn_date: txn.txn_date || new Date().toISOString().split('T')[0],
      quantity: txn.quantity,
      price: txn.price,
      amount: txn.amount || txn.quantity * txn.price,
      brokerage: txn.brokerage || 0,
      stt: txn.stt || 0,
      gst: txn.gst || 0,
      stamp_duty: txn.stamp_duty || 0,
      other_charges: txn.other_charges || 0,
      charges:
        (txn.brokerage || 0) +
        (txn.stt || 0) +
        (txn.gst || 0) +
        (txn.stamp_duty || 0) +
        (txn.other_charges || 0),
      notes: txn.notes || '',
      source: txn.source || 'MANUAL',
      is_interim: txn.is_interim ?? true,
    };

    const { error: txnError } = await supabase.from('portfolio_transactions').insert(txnRow);
    if (txnError) return { success: false, error: txnError.message };

    // Find or create holding
    const holdingSearch = {
      asset_type: txn.asset_type,
      symbol: txn.symbol || null,
      isin: txn.isin || null,
      name: txn.name || txn.asset_type,
      exchange: txn.exchange || null,
      folio_number: txn.folio_number || null,
      demat_account: txn.demat_account || null,
      maturity_date: txn.maturity_date || null,
    };

    const existing = await findDuplicate(holdingSearch, user.id, effectiveFMId);

    if (existing) {
      // Recalculate average buy price (avg_buy_price stays native-currency —
      // only invested_amount/current_value are INR-converted for STOCK_US)
      const oldQty = parseFloat(existing.quantity) || 0;
      const oldCost = parseFloat(existing.invested_amount) || 0;
      const newQty = oldQty + parseFloat(txn.quantity);
      const newCost = oldCost + parseFloat(txn.amount || txn.quantity * txn.price) * usdInrRate;
      const newAvg = newQty > 0 ? (oldQty * (parseFloat(existing.avg_buy_price) || 0) + parseFloat(txn.quantity) * parseFloat(txn.price)) / newQty : 0;
      const newCurrentPrice = (txn.current_price || existing.current_price || txn.price * usdInrRate);

      const { data, error } = await supabase
        .from('portfolio_holdings')
        .update({
          quantity: newQty,
          avg_buy_price: newAvg,
          invested_amount: newCost,
          current_price: newCurrentPrice,
          current_value: newQty * newCurrentPrice,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) return { success: false, error: error.message };
      return { success: true, data };
    }

    // Create new holding
    const { data, error } = await supabase
      .from('portfolio_holdings')
      .insert({
        user_id: user.id,
        family_member_id: effectiveFMId,
        asset_type: txn.asset_type,
        symbol: txn.symbol || null,
        isin: txn.isin || null,
        yahoo_symbol: txn.yahoo_symbol || null,
        name: txn.name || txn.asset_type,
        exchange: txn.exchange || null,
        quantity: txn.quantity,
        avg_buy_price: txn.price,
        invested_amount: (txn.amount || txn.quantity * txn.price) * usdInrRate,
        current_price: (txn.current_price || txn.price) * usdInrRate,
        current_value: txn.quantity * (txn.current_price || txn.price) * usdInrRate,
        currency: 'INR',
        source: 'MANUAL',
        source_identifier: 'MANUAL',
        source_priority: 5,
      })
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  } catch (err) {
    console.error('Error in processBuyTransaction:', err);
    return { success: false, error: err.message };
  }
};

// ===========================
// 8. processSellTransaction — proportional reduction
// ===========================
export const processSellTransaction = async (txn) => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const effectiveFMId = await resolveFamilyMemberId(user.id, txn.family_member_id);

    // Find existing holding
    const holdingSearch = {
      asset_type: txn.asset_type,
      symbol: txn.symbol || null,
      isin: txn.isin || null,
      name: txn.name || txn.asset_type,
      exchange: txn.exchange || null,
      folio_number: txn.folio_number || null,
      demat_account: txn.demat_account || null,
      maturity_date: txn.maturity_date || null,
    };

    const existing = await findDuplicate(holdingSearch, user.id, effectiveFMId);
    if (!existing) return { success: false, error: 'No holding found to sell from' };

    const oldQty = parseFloat(existing.quantity) || 0;
    const sellQty = parseFloat(txn.quantity);
    if (sellQty > oldQty)
      return { success: false, error: `Cannot sell ${sellQty}, only ${oldQty} held` };

    const avgBuyPrice = parseFloat(existing.avg_buy_price) || 0;
    const realizedGain = sellQty * (parseFloat(txn.price) - avgBuyPrice);

    // Insert transaction
    const txnRow = {
      user_id: user.id,
      family_member_id: effectiveFMId,
      holding_id: existing.id,
      asset_type: txn.asset_type,
      symbol: txn.symbol || null,
      isin: txn.isin || null,
      yahoo_symbol: txn.yahoo_symbol || null,
      name: txn.name || txn.asset_type,
      exchange: txn.exchange || null,
      txn_type: 'SELL',
      txn_date: txn.txn_date || new Date().toISOString().split('T')[0],
      quantity: sellQty,
      price: txn.price,
      amount: txn.amount || sellQty * txn.price,
      charges: txn.charges || 0,
      realized_gain: realizedGain,
      notes: txn.notes || '',
      source: txn.source || 'MANUAL',
      is_interim: txn.is_interim ?? true,
    };

    const { error: txnError } = await supabase.from('portfolio_transactions').insert(txnRow);
    if (txnError) return { success: false, error: txnError.message };

    // Update holding
    const newQty = oldQty - sellQty;

    if (newQty <= 0) {
      // Delete holding when fully sold
      await supabase.from('portfolio_holdings').delete().eq('id', existing.id);
      return { success: true, data: null, fullySold: true, realizedGain };
    }

    // Proportional cost reduction (avg buy price stays the same)
    const newCost = newQty * avgBuyPrice;
    const { data, error } = await supabase
      .from('portfolio_holdings')
      .update({
        quantity: newQty,
        invested_amount: newCost,
        current_value: newQty * (txn.current_price || existing.current_price || txn.price),
        updated_at: new Date().toISOString(),
      })
      .eq('id', existing.id)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data, realizedGain };
  } catch (err) {
    console.error('Error in processSellTransaction:', err);
    return { success: false, error: err.message };
  }
};

// ===========================
// 9. getTransactions
// ===========================
export const getTransactions = async (holdingId = null) => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated', data: [] };

    // Paginated fetch — Supabase default limit is 1000 rows
    const PAGE_SIZE = 1000;
    let allData = [];
    let offset = 0;

    while (true) {
      let query = supabase
        .from('portfolio_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('txn_date', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);

      if (holdingId) {
        query = query.eq('holding_id', holdingId);
      }

      const { data: page, error } = await query;
      if (error) return { success: false, error: error.message, data: [] };
      if (!page || page.length === 0) break;
      allData.push(...page);
      if (page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    return { success: true, data: allData };
  } catch (err) {
    return { success: false, error: err.message, data: [] };
  }
};

// ===========================
// 10. saveQuickEntry
// ===========================
export const saveQuickEntry = async (totalCorpus, allocation, familyMemberId = null) => {
  // allocation = { equity: 60, debt: 25, gold: 10, other: 5 }
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured' };
  }

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const effectiveFMId = await resolveFamilyMemberId(user.id, familyMemberId);

    // Delete existing quick entry holdings for this user
    await supabase
      .from('portfolio_holdings')
      .delete()
      .eq('user_id', user.id)
      .eq('source_identifier', 'QUICK_ENTRY');

    const holdings = [];
    const CATEGORY_MAP = {
      equity: { name: 'Equity (Estimated)', asset_type: 'MF' },
      debt: { name: 'Debt (Estimated)', asset_type: 'FD' },
      gold: { name: 'Gold (Estimated)', asset_type: 'GOLD_PHYSICAL' },
      other: { name: 'Other (Estimated)', asset_type: 'OTHER_FIXED' },
    };

    for (const [category, pct] of Object.entries(allocation)) {
      if (pct <= 0) continue;
      const amount = (totalCorpus * pct) / 100;
      const info = CATEGORY_MAP[category] || {
        name: `${category} (Estimated)`,
        asset_type: 'OTHER_FIXED',
      };

      holdings.push({
        user_id: user.id,
        family_member_id: effectiveFMId,
        asset_type: info.asset_type,
        name: info.name,
        quantity: 1,
        avg_buy_price: amount,
        invested_amount: amount,
        current_value: amount,
        source: 'QUICK_ENTRY',
        source_identifier: 'QUICK_ENTRY',
        source_priority: 10,
        currency: 'INR',
      });
    }

    if (holdings.length === 0) {
      return { success: false, error: 'No allocation provided' };
    }

    const { data, error } = await supabase.from('portfolio_holdings').insert(holdings).select();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  } catch (err) {
    console.error('Error in saveQuickEntry:', err);
    return { success: false, error: err.message };
  }
};

// ===========================
// 11. addBulkTransactions — batch insert CAS transactions with dedup
// ===========================
export const addBulkTransactions = async (transactions, familyMemberId = null) => {
  if (!isSupabaseConfigured() || !transactions || transactions.length === 0) {
    return { success: true, inserted: 0 };
  }

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: 'Not authenticated' };

    const effectiveFMId = await resolveFamilyMemberId(user.id, familyMemberId);

    const rows = transactions.map((txn) => ({
      user_id: user.id,
      family_member_id: effectiveFMId,
      asset_type: txn.isin?.startsWith('INE') ? 'STOCK_INDIAN' : 'MF',
      isin: txn.isin || null,
      name: txn.scheme_name || txn.name || 'Unknown',
      txn_type: txn.txn_type || 'OTHER',
      txn_date: txn.txn_date || null,
      quantity: txn.quantity || 0,
      price: txn.price || 0,
      amount: txn.amount || 0,
      external_id: txn.external_id || null,
      source: txn.source || 'CAS_UPLOAD',
      source_identifier: txn.source_identifier || 'NSDL_CAS',
      folio_number: txn.folio_number || null,
      metadata: { raw_type: txn.raw_type },
    }));

    // Batch insert with ON CONFLICT DO NOTHING (dedup via external_id)
    const batchSize = 100;
    let inserted = 0;

    for (let i = 0; i < rows.length; i += batchSize) {
      const batch = rows.slice(i, i + batchSize);
      const { error } = await supabase.from('portfolio_transactions').upsert(batch, {
        onConflict: 'user_id,external_id',
        ignoreDuplicates: true,
      });

      if (!error) {
        inserted += batch.length;
      } else {
        console.warn('Bulk transaction insert error:', error.message);
      }
    }

    console.log(`Bulk transactions: ${inserted} of ${transactions.length} inserted`);
    return { success: true, inserted };
  } catch (err) {
    console.error('Error in addBulkTransactions:', err);
    return { success: false, error: err.message };
  }
};

// ===========================
// Backward-compat aliases
// ===========================
export const getInvestments = getHoldings;
export const saveInvestments = upsertHoldingsBatch;
export const addInvestment = upsertHolding;
export const updateInvestment = updateHolding;
export const deleteInvestment = deleteHolding;

// ============================================
// LocalStorage Fallback Functions
// ============================================
const getHoldingsFromLocalStorage = () => {
  try {
    const saved = localStorage.getItem(LS_KEY);
    return { success: true, data: saved ? JSON.parse(saved) : [] };
  } catch (err) {
    return { success: false, error: err.message, data: [] };
  }
};

const saveHoldingsToLocalStorage = (holdings) => {
  try {
    const existing = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    const existingISINs = new Set(existing.filter((i) => i.isin).map((i) => i.isin));

    const newHoldings = holdings.map((h, idx) => ({
      id: h.id || `local-${Date.now()}-${idx}`,
      name: h.name,
      asset_type: h.asset_type || 'OTHER',
      isin: h.isin,
      symbol: h.symbol,
      quantity: h.quantity ?? h.units ?? null,
      avg_buy_price: h.avg_buy_price ?? h.avg_cost ?? null,
      invested_amount: h.invested_amount || 0,
      current_value: h.current_value || 0,
      source: h.source || 'MANUAL',
      source_identifier: h.source_identifier || 'MANUAL',
      // Backward-compat aliases
      units: h.quantity ?? h.units ?? null,
      avg_cost: h.avg_buy_price ?? h.avg_cost ?? null,
    }));

    const uniqueNew = newHoldings.filter((h) => !h.isin || !existingISINs.has(h.isin));
    const merged = [...existing, ...uniqueNew];

    localStorage.setItem(LS_KEY, JSON.stringify(merged));
    return { success: true, data: merged };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

const addHoldingToLocalStorage = (holding) => {
  try {
    const existing = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    const newHolding = {
      id: `local-${Date.now()}`,
      name: holding.name,
      asset_type: holding.asset_type || 'OTHER',
      isin: holding.isin,
      symbol: holding.symbol,
      quantity: holding.quantity ?? holding.units ?? null,
      avg_buy_price: holding.avg_buy_price ?? holding.avg_cost ?? null,
      invested_amount: holding.invested_amount || 0,
      current_value: holding.current_value || 0,
      source: holding.source || 'MANUAL',
      // Backward-compat aliases
      units: holding.quantity ?? holding.units ?? null,
      avg_cost: holding.avg_buy_price ?? holding.avg_cost ?? null,
    };
    existing.push(newHolding);
    localStorage.setItem(LS_KEY, JSON.stringify(existing));
    return { success: true, data: newHolding };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

const updateHoldingInLocalStorage = (id, updates) => {
  try {
    const existing = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    const index = existing.findIndex((h) => h.id === id);
    if (index === -1) return { success: false, error: 'Holding not found' };
    existing[index] = { ...existing[index], ...updates };
    localStorage.setItem(LS_KEY, JSON.stringify(existing));
    return { success: true, data: existing[index] };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

const deleteHoldingFromLocalStorage = (id) => {
  try {
    const existing = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
    const filtered = existing.filter((h) => h.id !== id);
    localStorage.setItem(LS_KEY, JSON.stringify(filtered));
    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
};
